/**
 * Format date according to regional settings
 * @param {string|Date} date - The date to format
 * @param {Object} settings - Regional settings object
 * @returns {string} Formatted date string
 */
export const formatDate = (date, settings = {}) => {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '-';
  
  const dateFormat = settings.date_format || 'DD/MM/YYYY';
  const timezone = settings.timezone || 'UTC';
  
  const day = dateObj.toLocaleString('en-GB', { day: '2-digit', timeZone: timezone });
  const month = dateObj.toLocaleString('en-GB', { month: '2-digit', timeZone: timezone });
  const monthShort = dateObj.toLocaleString('en-GB', { month: 'short', timeZone: timezone });
  const year = dateObj.toLocaleString('en-GB', { year: 'numeric', timeZone: timezone });
  
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD MMM YYYY':
      return `${day} ${monthShort} ${year}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Format date and time according to regional settings
 * @param {string|Date} date - The date to format
 * @param {Object} settings - Regional settings object
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, settings = {}) => {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '-';
  
  const timeFormat = settings.time_format || '24h';
  const timezone = settings.timezone || 'UTC';
  
  const datePart = formatDate(dateObj, settings);
  const timePart = dateObj.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
    timeZone: timezone
  });
  
  return `${datePart} ${timePart}`;
};

/**
 * Parse date from YYYYMMDD format
 * @param {string} dateStr - Date string in YYYYMMDD format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseDateFromYYYYMMDD = (dateStr) => {
  if (!dateStr || dateStr.length !== 8) return null;
  
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  
  const date = new Date(`${year}-${month}-${day}`);
  return isNaN(date.getTime()) ? null : date;
};
